/**
 * Chapter 5 — Generate lays out the day and nothing sends: the real
 * control's words (shared constants), the confirm dialog, the DRAFT rows
 * dropping in, the texts filling, and the toast — then the padlock.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Lock, Zap } from 'lucide-react';

import { Badge } from '@/components/ui';
import {
  GENERATE_CONFIRM_LABEL,
  GENERATE_CONFIRM_TITLE,
  GENERATE_DRAFTS_ONLY,
  GENERATE_FILL_LABEL,
  GENERATE_FILLING_NOTE,
  GENERATE_LABEL,
  GENERATE_YOU_SEND,
  generateSuccessTitle,
} from '@/features/drop-generator/components/board-header/generate-copy';
import { minutesLabel } from '@/features/drop-generator/utils/format';

import { DEMO_DAY } from '../../fixtures';
import { Press } from '../../motion/Accents';
import { FrameStack } from '../../motion/FrameStack';
import { Illustration } from '../../motion/Illustration';
import { Enter, Node } from '../../motion/Node';

const CYCLE = 12;
const ROWS = DEMO_DAY.containers[0].drops;
const { drops, sms } = DEMO_DAY.totals;
const day = `${DEMO_DAY.runDate.slice(8, 10)}/${DEMO_DAY.runDate.slice(5, 7)}`;
const BTN = 'inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold';

const Dialog: FC = () => (
  <Enter at={1.5}>
    <div className="rounded-lg border border-border bg-card p-3 text-xs shadow-sm">
      <p className="text-sm font-semibold">{GENERATE_CONFIRM_TITLE}</p>
      <p className="mt-1 text-muted-foreground">
        {drops} drops · {sms.toLocaleString('en-US')} SMS laid out for {day}. {GENERATE_DRAFTS_ONLY}
      </p>
      <p className="mt-1 text-muted-foreground">
        ☑ {GENERATE_FILL_LABEL} (≈{DEMO_DAY.aiCalls.texts} AI calls)
      </p>
      <p className="mt-2 flex justify-end gap-2">
        <span className={`${BTN} border border-input text-muted-foreground`}>Cancel</span>
        <Press at={3}><span className={`${BTN} bg-primary text-primary-foreground`}>{GENERATE_CONFIRM_LABEL}</span></Press>
      </p>
    </div>
  </Enter>
);

const Counter: FC = () => (
  <FrameStack
    cycleSeconds={CYCLE}
    className="text-xs"
    frames={[
      <span key="a" className="invisible whitespace-nowrap">filling texts</span>,
      <span key="b" className="whitespace-nowrap text-muted-foreground">filling texts 0/{drops}…</span>,
      <span key="c" className="whitespace-nowrap text-muted-foreground">filling texts {Math.ceil(drops / 2)}/{drops}…</span>,
      <span key="d" className="whitespace-nowrap font-medium text-success">{drops}/{drops} drafts ready to review</span>,
    ]}
  />
);

export const GenerateIllustration: FC = () => (
  <Illustration cycleSeconds={CYCLE}>
    <div className="grid gap-3 md:grid-cols-[260px_1fr]">
      <div className="flex flex-col gap-2">
        <Press at={0}><span className={`${BTN} bg-primary text-primary-foreground`}><Zap className="h-3.5 w-3.5" /> {GENERATE_LABEL}</span></Press>
        <Dialog />
        <Enter at={10}>
          <div className="rounded-lg border border-success/40 bg-success/10 p-2 text-xs">
            <p className="font-semibold">{generateSuccessTitle(drops, 0)}</p>
            <p className="text-muted-foreground">{GENERATE_FILLING_NOTE}</p>
          </div>
        </Enter>
      </div>
      <div className="flex flex-col gap-1 text-xs">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Prepared Drops — WARM UP</p>
        {ROWS.map((d, i) => (
          <Enter key={d.slotId} at={4 + i * 0.4}>
            <div className="flex items-center gap-2 rounded border border-border/60 bg-background px-2 py-1 font-mono">
              <span className="w-12 text-muted-foreground">{minutesLabel(d.minute)}</span>
              <span className="min-w-0 grow truncate">{d.listName}</span>
              <span className="tabular-nums">{d.amount.toLocaleString('en-US')}</span>
              <Badge tone="secondary">DRAFT</Badge>
            </div>
          </Enter>
        ))}
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <Counter />
          <span className="grow" />
          <Node at={11} className="border-primary/40 bg-primary/5 font-medium"><Lock className="h-3 w-3" /> {GENERATE_YOU_SEND}</Node>
        </div>
      </div>
    </div>
  </Illustration>
);
